export class ProductOptions extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['option', 'selected-variant']
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(this, 'change', this.onProductOptionChange.bind(this))

    for (const option of this.els.option.elements) {
      this.listeners.add(
        option,
        'mouseenter',
        this.onProductOptionEnter.bind(this),
      )
    }

    this._variants = this._parseVariants()
    this.filterInvalidOptions()
  }

  onProductOptionChange (event) {
    const target =
      event.target.tagName === 'SELECT'
        ? event.target.selectedOptions[0]
        : event.target

    this.filterInvalidOptions()

    window.BAO.dispatchEvent(window.BAO.EVENTS.PRODUCT.OPTION_SELECTED, {
      event,
      target,
      selectedOptionValues: this.selectedOptionValues,
    })
  }

  onProductOptionEnter (event) {
    window.BAO.dispatchEvent(window.BAO.EVENTS.PRODUCT.OPTION_ENTERED, {
      event,
      selectedOptionValues: this.selectedOptionValues,
    })
  }

  _parseVariants () {
    const el = this.querySelector('[data-product-options-el="variant-data"]')
    if (!el) return null

    try {
      return JSON.parse(el.innerHTML)
    } catch (e) {
      console.warn('[ProductOptions] Could not parse variant-data JSON', e)
      return null
    }
  }

  filterInvalidOptions () {
    if (!this._variants) return

    const fieldsets = Array.from(
      this.querySelectorAll('[data-variant-radios-el="option"]'),
    )

    if (fieldsets.length === 0) return

    const currentSelections = fieldsets.map((fs) => {
      const checked = fs.querySelector('input[type="radio"]:checked')
      return checked ? checked.value : null
    })

    for (let i = 0; i < fieldsets.length; i++) {
      const matchingVariants = this._variants.filter((variant) => {
        return fieldsets.every((_, j) => {
          if (j === i) return true
          const selectedVal = currentSelections[j]
          if (selectedVal === null) return true
          return variant[`option${j + 1}`] === selectedVal
        })
      })

      const validValues = [
        ...new Set(
          matchingVariants.map((v) => v[`option${i + 1}`]).filter(Boolean),
        ),
      ]

      this._applyVisibility(fieldsets[i], validValues, currentSelections[i])
    }
  }

  _applyVisibility (fieldset, validValues, currentVal) {
    const optionWrappers = Array.from(
      fieldset.querySelectorAll('[data-product-options-el="option"]'),
    )

    let currentValStillVisible = false

    for (const wrapper of optionWrappers) {
      const optionValue = wrapper.dataset.optionValue
      if (!optionValue) continue

      const isValid = validValues.includes(optionValue)

      if (isValid) {
        wrapper.hidden = false
        wrapper.removeAttribute('aria-hidden')
        if (optionValue === currentVal) {
          currentValStillVisible = true
        }
      } else {
        wrapper.hidden = true
        wrapper.setAttribute('aria-hidden', 'true')

        const radio = wrapper.querySelector('input[type="radio"]')
        if (radio && radio.checked) {
          radio.checked = false
        }
      }
    }

    if (!currentValStillVisible && currentVal !== null) {
      const firstVisible = optionWrappers.find((w) => !w.hidden)
      if (firstVisible) {
        const radio = firstVisible.querySelector('input[type="radio"]')
        if (radio) {
          radio.checked = true
          radio.dispatchEvent(new Event('change', { bubbles: true }))
        }
      }
    }
  }

  get selectedOptionValues () {
    const selectedOptions = Array.from(this.querySelectorAll('select')).flatMap(
      (select) =>
        Array.from(select.selectedOptions).map(
          (option) => option.dataset.optionValueId,
        ),
    )

    const checkedInputs = Array.from(
      this.querySelectorAll('fieldset input:checked'),
    ).map((input) => input.dataset.optionValueId)

    return [...selectedOptions, ...checkedInputs]
  }

  get selectedVariant () {
    return JSON.parse(this.els['selected-variant'].element.innerHTML)
  }
}

if (!customElements.get('product-options')) {
  customElements.define('product-options', ProductOptions)
}
